import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  motion,
  useScroll,
  useTransform,
  useMotionValueEvent,
  useInView,
  animate,
  useMotionValue,
  useSpring,
} from 'framer-motion';
import {
  Sparkles, Database, ArrowRight, BarChart3, Brain, Search,
  LineChart, FileText, Zap, Users, Shield, Building2, Scale,
  UserCheck, Receipt, Network, ScrollText, Megaphone, CreditCard,
  GitCompare, SlidersHorizontal, MessageSquare, Briefcase,
  Landmark, BarChart2, ChevronRight, Terminal, MousePointer2,
} from 'lucide-react';

/* ────────────────────────────────────────────────────────────
   CANVAS BACKGROUND — interactive particle mesh
   ──────────────────────────────────────────────────────────── */

function ParticleCanvas() {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: -9999, y: -9999 });
  const particles = useRef([]);
  const raf = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H;

    function resize() {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    const N = Math.min(80, Math.floor((window.innerWidth * window.innerHeight) / 16000));
    particles.current = Array.from({ length: N }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      r: Math.random() * 1.5 + 0.5,
    }));

    function draw() {
      ctx.clearRect(0, 0, W, H);
      const mx = mouse.current.x;
      const my = mouse.current.y;
      const pts = particles.current;

      for (let i = 0; i < pts.length; i++) {
        const p = pts[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = W;
        if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H;
        if (p.y > H) p.y = 0;

        const dx = mx - p.x;
        const dy = my - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const glow = dist < 220 ? 1 - dist / 220 : 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r + glow * 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(52,211,153,${0.15 + glow * 0.6})`;
        ctx.fill();

        for (let j = i + 1; j < pts.length; j++) {
          const q = pts[j];
          const lx = p.x - q.x;
          const ly = p.y - q.y;
          const ld = Math.sqrt(lx * lx + ly * ly);
          if (ld < 140) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = `rgba(52,211,153,${0.04 * (1 - ld / 140)})`;
            ctx.stroke();
          }
        }
      }
      raf.current = requestAnimationFrame(draw);
    }
    draw();

    const onMouse = (e) => {
      mouse.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('mousemove', onMouse);
    return () => {
      cancelAnimationFrame(raf.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouse);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-0 pointer-events-none"
      style={{ opacity: 0.6 }}
    />
  );
}

/* ────────────────────────────────────────────────────────────
   MOUSE SPOTLIGHT — radial glow follows cursor
   ──────────────────────────────────────────────────────────── */

function MouseSpotlight() {
  const x = useMotionValue(-200);
  const y = useMotionValue(-200);
  const sx = useSpring(x, { stiffness: 120, damping: 30 });
  const sy = useSpring(y, { stiffness: 120, damping: 30 });

  useEffect(() => {
    const handler = (e) => { x.set(e.clientX); y.set(e.clientY); };
    window.addEventListener('mousemove', handler);
    return () => window.removeEventListener('mousemove', handler);
  }, [x, y]);

  return (
    <motion.div
      className="fixed z-[1] pointer-events-none w-[600px] h-[600px] rounded-full"
      style={{
        x: sx, y: sy,
        translateX: '-50%', translateY: '-50%',
        background: 'radial-gradient(circle, rgba(16,185,129,0.07) 0%, transparent 70%)',
      }}
    />
  );
}

/* ────────────────────────────────────────────────────────────
   ANIMATED COUNTER
   ──────────────────────────────────────────────────────────── */

function Counter({ to, suffix = '', label }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-15%' });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const c = animate(0, to, {
      duration: 2.4, ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => c.stop();
  }, [inView, to]);

  return (
    <div ref={ref} className="text-center">
      <span className="text-4xl md:text-5xl font-black tracking-tighter bg-gradient-to-br from-white via-emerald-200 to-emerald-400 bg-clip-text text-transparent tabular-nums">
        {val.toLocaleString()}{suffix}
      </span>
      <p className="text-xs text-zinc-500 mt-2 tracking-wide uppercase font-medium">{label}</p>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   TYPING TERMINAL — auto-plays once visible
   ──────────────────────────────────────────────────────────── */

const TERMINAL_LINES = [
  { type: 'cmd', text: '> kubair ask "Ultratech vs Ambuja — financials + insider activity"' },
  { type: 'info', text: '  Querying company_profile, financials, insider_trades, peer_compare...' },
  { type: 'info', text: '  4 tools completed in 1.2s' },
  { type: 'blank', text: '' },
  { type: 'result', text: '  Metric           Ultratech    Ambuja' },
  { type: 'result', text: '  Revenue (FY25)   ₹74,936 Cr   ₹33,142 Cr' },
  { type: 'result', text: '  EBITDA Margin    17.6%         19.2%' },
  { type: 'result', text: '  PE Ratio         42.3x         31.8x' },
  { type: 'result', text: '  Promoter Hold    56.4%         63.1%' },
  { type: 'blank', text: '' },
  { type: 'insight', text: '  → Ambuja: stronger margins + recent insider buying at lower multiples.' },
  { type: 'insight', text: '  → Ultratech: scale premium justified, but entry matters vs. cycle.' },
];

function TypingTerminal() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-10%' });
  const [lines, setLines] = useState([]);
  const [charIdx, setCharIdx] = useState(0);
  const [lineIdx, setLineIdx] = useState(0);

  useEffect(() => {
    if (!inView) return;
    if (lineIdx >= TERMINAL_LINES.length) return;

    const currentLine = TERMINAL_LINES[lineIdx];
    if (currentLine.type === 'blank') {
      const t = setTimeout(() => {
        setLines((p) => [...p, { ...currentLine, displayed: '' }]);
        setLineIdx((i) => i + 1);
        setCharIdx(0);
      }, 120);
      return () => clearTimeout(t);
    }

    if (charIdx <= currentLine.text.length) {
      const speed = currentLine.type === 'cmd' ? 22 : currentLine.type === 'info' ? 12 : 8;
      const t = setTimeout(() => {
        if (charIdx === currentLine.text.length) {
          setLines((p) => {
            const next = [...p];
            if (next.length && next[next.length - 1].lineIdx === lineIdx) {
              next[next.length - 1].displayed = currentLine.text;
            }
            return next;
          });
          setLineIdx((i) => i + 1);
          setCharIdx(0);
        } else {
          if (charIdx === 0) {
            setLines((p) => [...p, { ...currentLine, displayed: '', lineIdx }]);
          }
          setLines((p) => {
            const next = [...p];
            if (next.length) {
              next[next.length - 1].displayed = currentLine.text.slice(0, charIdx + 1);
            }
            return next;
          });
          setCharIdx((c) => c + 1);
        }
      }, speed);
      return () => clearTimeout(t);
    }
  }, [inView, lineIdx, charIdx]);

  const colorOf = (type) => {
    if (type === 'cmd') return 'text-emerald-400';
    if (type === 'info') return 'text-zinc-500';
    if (type === 'insight') return 'text-amber-300/90';
    return 'text-zinc-300';
  };

  const isTyping = lineIdx < TERMINAL_LINES.length;

  return (
    <div ref={ref} className="relative group">
      <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-emerald-500/30 via-transparent to-teal-500/20 opacity-60 group-hover:opacity-100 transition-opacity blur-sm" />
      <div className="relative rounded-2xl bg-zinc-950 border border-white/[0.08] overflow-hidden shadow-2xl shadow-emerald-950/40">
        {/* Title bar */}
        <div className="flex items-center gap-2 px-4 py-3 bg-zinc-900/70 border-b border-white/[0.06]">
          <div className="flex gap-1.5">
            <span className="w-3 h-3 rounded-full bg-zinc-700 hover:bg-red-400/80 transition-colors" />
            <span className="w-3 h-3 rounded-full bg-zinc-700 hover:bg-amber-400/80 transition-colors" />
            <span className="w-3 h-3 rounded-full bg-zinc-700 hover:bg-emerald-400/80 transition-colors" />
          </div>
          <div className="flex-1 text-center">
            <span className="text-[11px] text-zinc-500 font-mono">kubair — query</span>
          </div>
          <Terminal className="w-3.5 h-3.5 text-zinc-600" />
        </div>
        {/* Body */}
        <div className="p-5 font-mono text-[13px] leading-relaxed min-h-[320px] md:min-h-[360px] space-y-0.5 overflow-hidden">
          {lines.map((l, i) => (
            <div key={i} className={`${colorOf(l.type)} whitespace-pre`}>
              {l.displayed}
            </div>
          ))}
          {isTyping && (
            <span className="inline-block w-2 h-4 bg-emerald-400/80 animate-pulse ml-px" />
          )}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   MARQUEE — continuous horizontal scroll of data tools
   ──────────────────────────────────────────────────────────── */

const TOOL_ITEMS = [
  { icon: Building2, label: 'Company Profile' },
  { icon: BarChart3, label: 'Financials' },
  { icon: Scale, label: 'Ratios' },
  { icon: Users, label: 'Shareholding' },
  { icon: UserCheck, label: 'Insider Trades' },
  { icon: Receipt, label: 'Corporate Actions' },
  { icon: Network, label: 'Director Network' },
  { icon: MessageSquare, label: 'X Signals' },
  { icon: ScrollText, label: 'Filing Search' },
  { icon: Megaphone, label: 'BSE Notices' },
  { icon: CreditCard, label: 'MCA Charges' },
  { icon: GitCompare, label: 'Peer Compare' },
  { icon: SlidersHorizontal, label: 'Universe Screener' },
  { icon: Shield, label: 'Quality Gates' },
  { icon: Database, label: 'Market Data' },
  { icon: Brain, label: 'AI Synthesis' },
];

function Marquee({ reverse = false }) {
  const doubled = [...TOOL_ITEMS, ...TOOL_ITEMS];
  return (
    <div className="overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
      <div
        className={`flex gap-4 w-max ${reverse ? 'animate-[marquee-reverse_60s_linear_infinite]' : 'animate-[marquee_60s_linear_infinite]'}`}
      >
        {doubled.map((t, i) => (
          <div
            key={`${t.label}-${i}`}
            className="flex items-center gap-2.5 px-4 py-2.5 rounded-full border border-white/[0.06] bg-zinc-900/50 backdrop-blur-sm hover:border-emerald-500/30 hover:bg-emerald-500/[0.06] transition-all duration-300 shrink-0 group cursor-default"
          >
            <t.icon className="w-4 h-4 text-zinc-500 group-hover:text-emerald-400 transition-colors" />
            <span className="text-xs text-zinc-400 group-hover:text-zinc-200 transition-colors font-medium whitespace-nowrap">
              {t.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   BENTO FEATURE GRID
   ──────────────────────────────────────────────────────────── */

function BentoCard({ children, className = '', delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 32, scale: 0.97 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: '-8%' }}
      transition={{ delay, type: 'spring', stiffness: 180, damping: 24 }}
      className={`group relative rounded-3xl border border-white/[0.07] bg-zinc-900/30 backdrop-blur-md overflow-hidden transition-all duration-500 hover:border-emerald-500/25 hover:shadow-lg hover:shadow-emerald-500/[0.08] ${className}`}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/[0.04] via-transparent to-teal-500/[0.03] opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
      {children}
    </motion.div>
  );
}

function BentoGrid() {
  return (
    <section id="features" className="relative py-24 md:py-36 overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-emerald-400 text-xs font-semibold uppercase tracking-[0.25em] mb-3">
            The platform
          </p>
          <h2 className="text-4xl md:text-6xl font-black text-white tracking-tight">
            Six pillars. Zero noise.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-5 auto-rows-[minmax(200px,auto)]">
          {/* AI Chat — wide */}
          <BentoCard className="md:col-span-4 md:row-span-2" delay={0}>
            <div className="p-8 h-full flex flex-col">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center ring-1 ring-emerald-500/30">
                  <Brain className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">AI Chat</h3>
                  <p className="text-xs text-zinc-500">16 data tools. Cited answers. No hallucination.</p>
                </div>
              </div>
              <div className="flex-1 rounded-xl bg-zinc-950/80 border border-white/[0.05] p-4 overflow-hidden">
                <div className="space-y-3">
                  <div className="flex justify-end">
                    <div className="bg-emerald-600 text-white text-sm rounded-2xl rounded-tr-md px-4 py-2 max-w-[80%]">
                      What's driving Reliance's margin compression this quarter?
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {['financials', 'earnings_transcript', 'peer_compare', 'market_data'].map((t) => (
                      <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400/80 border border-emerald-500/20 font-mono">{t}</span>
                    ))}
                  </div>
                  <div className="bg-zinc-800/50 border border-white/[0.05] rounded-2xl rounded-tl-md px-4 py-3 max-w-[95%]">
                    <p className="text-sm text-zinc-300 leading-relaxed">O2C segment margins contracted 340 bps QoQ to 6.8%, dragged by weak refining cracks (Singapore GRM at $4.2/bbl vs $8.1 last quarter). Jio and Retail continue to offset...</p>
                  </div>
                </div>
              </div>
            </div>
          </BentoCard>

          {/* Stock Screener — tall right */}
          <BentoCard className="md:col-span-2 md:row-span-2" delay={0.08}>
            <div className="p-6 h-full flex flex-col">
              <div className="flex items-center gap-2 mb-4">
                <Search className="w-5 h-5 text-emerald-400" />
                <h3 className="text-lg font-bold text-white">Screener</h3>
              </div>
              <p className="text-xs text-zinc-500 mb-4">Natural language → instant results</p>
              <div className="flex-1 bg-zinc-950/70 rounded-xl border border-white/[0.05] p-3 font-mono text-[11px]">
                <div className="text-emerald-400/70 mb-3 text-[10px]">
                  &gt; IT companies, ROE &gt; 20%, PE &lt; 30
                </div>
                <div className="space-y-2">
                  {[
                    { sym: 'TCS', roe: '43%', pe: '28x', c: true },
                    { sym: 'INFY', roe: '32%', pe: '24x', c: true },
                    { sym: 'HCLTECH', roe: '24%', pe: '26x', c: false },
                    { sym: 'WIPRO', roe: '16%', pe: '22x', c: false },
                    { sym: 'LTIM', roe: '28%', pe: '29x', c: true },
                  ].map((s) => (
                    <div key={s.sym} className={`flex justify-between py-1.5 px-2 rounded-lg transition-colors ${s.c ? 'text-zinc-200 bg-emerald-500/[0.08]' : 'text-zinc-500'}`}>
                      <span className="font-semibold">{s.sym}</span>
                      <span>{s.roe}</span>
                      <span>{s.pe}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </BentoCard>

          {/* Signals — medium */}
          <BentoCard className="md:col-span-2" delay={0.12}>
            <div className="p-6">
              <Zap className="w-5 h-5 text-amber-400 mb-3" />
              <h3 className="text-lg font-bold text-white mb-1">Smart Signals</h3>
              <p className="text-xs text-zinc-500 mb-4">Insider buys, FII convergence, promoter increases — surfaced before they trend.</p>
              <div className="space-y-2">
                {[
                  { color: 'bg-emerald-500', text: 'Promoter +2.4% in CDSL' },
                  { color: 'bg-amber-500', text: 'FII+DII convergence: IEX' },
                  { color: 'bg-rose-500', text: 'Insider sell cluster: IRCTC' },
                ].map((s) => (
                  <div key={s.text} className="flex items-center gap-2.5 py-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${s.color} shrink-0`} />
                    <span className="text-xs text-zinc-400">{s.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </BentoCard>

          {/* IC Memos */}
          <BentoCard className="md:col-span-2" delay={0.16}>
            <div className="p-6">
              <FileText className="w-5 h-5 text-blue-400 mb-3" />
              <h3 className="text-lg font-bold text-white mb-1">IC Memos</h3>
              <p className="text-xs text-zinc-500 mb-4">Turn any AI conversation into a client-ready PDF memo in one click.</p>
              <div className="bg-zinc-950/80 rounded-xl border border-white/[0.05] p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-7 rounded bg-blue-500/20 flex items-center justify-center"><FileText className="w-3 h-3 text-blue-400" /></div>
                  <div>
                    <p className="text-[11px] text-white font-medium">Ultratech_IC_Note.pdf</p>
                    <p className="text-[9px] text-zinc-600">Generated 2 min ago</p>
                  </div>
                </div>
                <div className="space-y-1 mt-2">
                  <div className="h-1.5 rounded-full bg-zinc-800 w-full" />
                  <div className="h-1.5 rounded-full bg-zinc-800 w-4/5" />
                  <div className="h-1.5 rounded-full bg-zinc-800 w-2/3" />
                </div>
              </div>
            </div>
          </BentoCard>

          {/* Portfolio */}
          <BentoCard className="md:col-span-2" delay={0.2}>
            <div className="p-6">
              <LineChart className="w-5 h-5 text-cyan-400 mb-3" />
              <h3 className="text-lg font-bold text-white mb-1">Portfolio Engine</h3>
              <p className="text-xs text-zinc-500 mb-4">Holdings, risk, AI reviews — performance narratives you can export.</p>
              <div className="flex items-end gap-0.5 h-20">
                {[28, 42, 35, 55, 48, 62, 58, 70, 65, 80, 72, 88, 82, 90, 86, 94].map((h, i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-t bg-gradient-to-t from-cyan-900/40 to-cyan-400/60 group-hover:to-cyan-400/80 transition-colors"
                    style={{ height: `${h}%` }}
                  />
                ))}
              </div>
            </div>
          </BentoCard>
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
   NAVBAR
   ──────────────────────────────────────────────────────────── */

function Navbar() {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const lastY = useRef(0);

  useMotionValueEvent(scrollY, 'change', (y) => {
    setScrolled(y > 20);
    const d = y - lastY.current;
    lastY.current = y;
    if (y < 80) setHidden(false);
    else if (d > 8) setHidden(true);
    else if (d < -8) setHidden(false);
  });

  return (
    <motion.nav
      initial={false}
      animate={{ y: hidden ? -100 : 0 }}
      transition={{ type: 'spring', stiffness: 400, damping: 40 }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${scrolled ? 'bg-zinc-950/90 backdrop-blur-2xl border-b border-white/[0.05]' : 'bg-transparent'}`}
    >
      <div className="max-w-7xl mx-auto h-16 flex items-center justify-between px-5 lg:px-8">
        <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-3 group">
          <img
            src="/kubair-mark.png"
            alt="KubAIr"
            className="w-14 h-14 object-contain drop-shadow-[0_0_14px_rgba(56,189,248,0.4)] group-hover:drop-shadow-[0_0_20px_rgba(56,189,248,0.6)] transition"
          />
          <span className="font-bold text-white text-xl tracking-tight">Kub<span className="bg-gradient-to-r from-emerald-300 via-sky-300 to-violet-400 bg-clip-text text-transparent">AI</span>r</span>
        </button>

        <div className="hidden md:flex items-center gap-8 text-sm text-zinc-500">
          <a href="/healthcheck" className="hover:text-white transition-colors duration-200 text-emerald-400 font-medium">
            Free Health Check
          </a>
          <a href="/one-stock" className="hover:text-white transition-colors duration-200">
            Daily pick
          </a>
          {[['#features', 'Features'], ['#demo', 'Demo'], ['#tools', 'Data tools']].map(([h, l]) => (
            <a key={h} href={h} className="hover:text-white transition-colors duration-200">{l}</a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button type="button" onClick={() => navigate('/login')} className="text-sm text-zinc-400 hover:text-white px-3 py-2 rounded-lg hover:bg-white/5 transition-all">
            Log in
          </button>
          <motion.button
            type="button"
            onClick={() => navigate('/signup')}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            className="text-sm font-semibold bg-white text-zinc-950 px-4 py-2 rounded-lg hover:bg-emerald-400 transition-colors duration-200"
          >
            Get started
          </motion.button>
        </div>
      </div>
    </motion.nav>
  );
}

/* ────────────────────────────────────────────────────────────
   HERO
   ──────────────────────────────────────────────────────────── */

function Hero() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);

  return (
    <motion.section
      ref={ref}
      style={{ opacity: heroOpacity, scale: heroScale }}
      className="relative min-h-[100dvh] flex items-center justify-center pt-16"
    >
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.85, filter: 'blur(10px)' }}
          animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-center mb-8"
        >
          <img
            src="/kubair-mark.png"
            alt="KubAIr"
            className="w-36 h-36 sm:w-44 sm:h-44 md:w-52 md:h-52 object-contain drop-shadow-[0_0_40px_rgba(56,189,248,0.35)]"
          />
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-2 text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white"
          >
            Kub<span className="bg-gradient-to-r from-emerald-300 via-sky-300 to-violet-400 bg-clip-text text-transparent">AI</span>r
          </motion.span>
        </motion.div>
        <h1 className="mb-6 mt-4">
          {['Market', 'edge'].map((w, i) => (
            <motion.span
              key={w}
              initial={{ opacity: 0, y: 40, filter: 'blur(8px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ delay: 0.1 + i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="inline-block text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-white tracking-tight mr-[0.22em]"
            >
              {w}
            </motion.span>
          ))}
          <br />
          {['distilled.'].map((w, i) => (
            <motion.span
              key={w}
              initial={{ opacity: 0, y: 40, filter: 'blur(8px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ delay: 0.42, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="inline-block text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black tracking-tight bg-gradient-to-r from-emerald-300 via-sky-400 to-violet-400 bg-clip-text text-transparent"
            >
              {w}
            </motion.span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.6 }}
          className="text-lg md:text-xl text-zinc-400 max-w-2xl mx-auto leading-relaxed mb-10"
        >
          AI that queries 16 proprietary data tools across 5,000+ listed names.
          <br className="hidden sm:block" />
          From raw question to IC-ready memo — in the time it takes to pour a coffee.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-3"
        >
          <motion.button
            type="button"
            onClick={() => navigate('/healthcheck')}
            whileHover={{ scale: 1.03, boxShadow: '0 0 50px rgba(52,211,153,0.45)' }}
            whileTap={{ scale: 0.97 }}
            className="group inline-flex items-center gap-2 bg-emerald-400 text-zinc-950 font-bold px-6 py-4 rounded-2xl text-base shadow-xl shadow-emerald-400/20 hover:bg-emerald-300 transition-colors"
          >
            Free Health Check — no signup <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </motion.button>
          <motion.button
            type="button"
            onClick={() => navigate('/one-stock')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center gap-2 bg-white/10 text-white border border-white/20 hover:bg-white/15 px-6 py-4 rounded-2xl text-base transition-all"
          >
            See today's pick <ChevronRight className="w-4 h-4" />
          </motion.button>
          <motion.button
            type="button"
            onClick={() => navigate('/signup')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center gap-2 border border-emerald-400/40 text-emerald-300 hover:text-emerald-200 hover:border-emerald-300/60 px-6 py-4 rounded-2xl text-base transition-all"
          >
            Sign up for full access <ChevronRight className="w-4 h-4" />
          </motion.button>
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.5 }}
          className="mt-5 text-xs text-zinc-500 max-w-xl mx-auto"
        >
          KubAIr Research (for investors) — SEBI Research Analyst registered. KubAIr Advisor OS (for SEBI-registered firms) — software that embeds your firm&apos;s own registration on every client output.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-16 flex items-center justify-center gap-2 text-xs text-zinc-600"
        >
          <MousePointer2 className="w-3 h-3" />
          <span>Move your cursor — the background reacts to you</span>
        </motion.div>
      </div>
    </motion.section>
  );
}

/* ────────────────────────────────────────────────────────────
   NUMBERS STRIP
   ──────────────────────────────────────────────────────────── */

function Numbers() {
  return (
    <section className="py-20 md:py-28 border-y border-white/[0.04]">
      <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-12">
        <Counter to={5000} suffix="+" label="Listed securities" />
        <Counter to={16} suffix="" label="Data tools" />
        <Counter to={15} suffix="s" label="Avg. query time" />
        <Counter to={100} suffix="%" label="Cited answers" />
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
   LIVE DEMO (terminal)
   ──────────────────────────────────────────────────────────── */

function DemoSection() {
  return (
    <section id="demo" className="relative py-24 md:py-36 overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-emerald-600/[0.06] blur-[150px] pointer-events-none" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-emerald-400 text-xs font-semibold uppercase tracking-[0.25em] mb-3">Live demo</p>
            <h2 className="text-3xl md:text-5xl font-black text-white tracking-tight mb-5">
              Watch it think.
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-6">
              KubAIr doesn't just search — it orchestrates. A single question triggers a cascade of proprietary tools:
              company profiles, financial statements, insider trade logs, peer comparisons, and more.
            </p>
            <p className="text-zinc-500 text-sm leading-relaxed mb-8">
              The terminal on the right types in real-time when you scroll here — showing you the exact tool pipeline and output, just like the real product.
            </p>
            <div className="flex items-center gap-4 text-xs text-zinc-600">
              <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> 4 tools per query</span>
              <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Cited sources</span>
              <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-cyan-500" /> No hallucination</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <TypingTerminal />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
   DATA TOOLS MARQUEE
   ──────────────────────────────────────────────────────────── */

function ToolsSection() {
  return (
    <section id="tools" className="py-24 md:py-32 overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <p className="text-emerald-400 text-xs font-semibold uppercase tracking-[0.25em] mb-3">Data moat</p>
          <h2 className="text-3xl md:text-5xl font-black text-white tracking-tight mb-3">
            Sixteen engines. One brain.
          </h2>
          <p className="text-zinc-500 max-w-lg mx-auto text-sm">Every answer draws from real-time proprietary data — never generic web scrapes.</p>
        </motion.div>
      </div>
      <div className="space-y-4">
        <Marquee />
        <Marquee reverse />
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
   AUDIENCE
   ──────────────────────────────────────────────────────────── */

const AUDIENCES = [
  { icon: BarChart2, title: 'Analysts', desc: 'Deep-dives with cited context. IC memos from live sessions. Peer sets in clicks.' },
  { icon: Briefcase, title: 'Portfolio Managers', desc: 'Universe screens on fundamentals. Risk flags with narrative. Signals before they trend.' },
  { icon: Landmark, title: 'Advisors', desc: 'Client-ready PDFs. Model portfolios. Research → recommendation → report in one pane.' },
];

function Audience() {
  return (
    <section className="py-24 md:py-32">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-emerald-400 text-xs font-semibold uppercase tracking-[0.25em] mb-3">Built for</p>
          <h2 className="text-3xl md:text-5xl font-black text-white tracking-tight">
            Professionals who move markets
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-px rounded-3xl border border-white/[0.06] overflow-hidden bg-white/[0.03]">
          {AUDIENCES.map((a, i) => (
            <motion.div
              key={a.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, type: 'spring', stiffness: 200, damping: 24 }}
              className="p-8 md:p-10 bg-zinc-950/90 backdrop-blur-sm hover:bg-zinc-900/60 transition-colors duration-500"
            >
              <a.icon className="w-7 h-7 text-emerald-400 mb-5" />
              <h3 className="text-xl font-bold text-white mb-3">{a.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{a.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
   CTA
   ──────────────────────────────────────────────────────────── */

function CTA() {
  const navigate = useNavigate();
  return (
    <section className="relative py-32 md:py-44 overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-emerald-950/20 to-zinc-950" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(90vw,600px)] h-[min(90vw,600px)] rounded-full bg-emerald-500/10 blur-[140px]" />
      </div>
      <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-4xl sm:text-5xl md:text-7xl font-black text-white tracking-tight leading-[1.05] mb-6"
        >
          Stop searching.
          <br />
          <span className="bg-gradient-to-r from-emerald-300 via-white to-teal-200 bg-clip-text text-transparent">Start deciding.</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-zinc-500 mb-12 max-w-md mx-auto"
        >
          Join the Decision OS for Indian public markets. Free to start — no credit card.
        </motion.p>
        <motion.button
          type="button"
          onClick={() => navigate('/signup')}
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          whileHover={{ scale: 1.05, boxShadow: '0 0 80px rgba(52,211,153,0.4)' }}
          whileTap={{ scale: 0.97 }}
          className="inline-flex items-center gap-2 bg-white text-zinc-950 font-bold px-12 py-5 rounded-2xl text-lg shadow-2xl shadow-white/10 hover:bg-emerald-400 transition-colors"
        >
          Get started free <ArrowRight className="w-5 h-5" />
        </motion.button>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
   ADVISOR CTA — book-a-demo for SEBI-registered firms
   ──────────────────────────────────────────────────────────── */

function AdvisorCTA() {
  const [form, setForm] = useState({ name: '', email: '', firm: '', sebi_reg: '', aum: '', phone: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.firm) {
      setError('Name, work email, and firm are required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || '';
      // Best-effort — the /api/leads endpoint may not exist yet. Posting anyway so
      // it just works once wired, and falling back to capturing locally otherwise.
      await fetch(`${backend}/api/leads/advisor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, source: 'landing_advisor_cta' }),
      }).catch(() => null);
      setSubmitted(true);
    } catch (err) {
      setError('Could not submit. Please email hello@kubair.in.');
    }
    setSubmitting(false);
  };

  return (
    <section id="advisor-cta" className="relative py-24 md:py-32 border-t border-white/[0.04]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-12 md:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-emerald-400 text-xs font-semibold uppercase tracking-[0.25em] mb-3">KubAIr Advisor OS</p>
          <h2 className="text-3xl md:text-5xl font-black text-white tracking-tight mb-5">
            Run your SEBI-registered advisory on one platform.
          </h2>
          <p className="text-zinc-400 leading-relaxed mb-6">
            Clients, proposals, compliance approvals, model portfolios, rebalancing, and daily client intelligence —
            with every output carrying <span className="text-white font-medium">your firm&apos;s</span> SEBI registration.
            KubAIr is the software vendor, not the adviser.
          </p>
          <ul className="text-sm text-zinc-400 space-y-2.5">
            <li className="flex gap-2.5"><span className="text-emerald-400 mt-0.5">✓</span> Proposal workflow with org-level compliance review + durable audit log</li>
            <li className="flex gap-2.5"><span className="text-emerald-400 mt-0.5">✓</span> Client mandates (IPS) captured; e-sign integration on accept</li>
            <li className="flex gap-2.5"><span className="text-emerald-400 mt-0.5">✓</span> Daily AI briefings per client with action items — on your brand</li>
            <li className="flex gap-2.5"><span className="text-emerald-400 mt-0.5">✓</span> RBAC, team chat, shared watchlists, research boards</li>
            <li className="flex gap-2.5"><span className="text-emerald-400 mt-0.5">✓</span> DPA + signed license certifying your firm holds the SEBI registration</li>
          </ul>
          <p className="mt-6 text-xs text-zinc-500">
            Typical customer: RIA, PMS, family office, boutique research house. From ₹9,999/mo.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-zinc-900/60 border border-white/[0.06] rounded-2xl p-6 md:p-8"
        >
          {submitted ? (
            <div className="text-center space-y-3 py-4">
              <div className="inline-flex w-12 h-12 rounded-full bg-emerald-500/15 text-emerald-400 items-center justify-center text-2xl">✓</div>
              <h3 className="text-lg font-bold text-white">Thanks — we&apos;ll be in touch within 1 business day</h3>
              <p className="text-sm text-zinc-400">
                If urgent, email <a href="mailto:hello@kubair.in" className="text-emerald-400 hover:underline">hello@kubair.in</a>.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <h3 className="text-lg font-bold text-white mb-1">Book a 30-min demo</h3>
              <p className="text-xs text-zinc-500 mb-4">
                Demos are hosted by the founder. SEBI-registered firms only.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name"
                  className="col-span-2 md:col-span-1 bg-zinc-950 border border-white/[0.06] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/50" />
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Work email"
                  className="col-span-2 md:col-span-1 bg-zinc-950 border border-white/[0.06] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/50" />
                <input required value={form.firm} onChange={(e) => setForm({ ...form, firm: e.target.value })} placeholder="Firm name"
                  className="col-span-2 bg-zinc-950 border border-white/[0.06] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/50" />
                <input value={form.sebi_reg} onChange={(e) => setForm({ ...form, sebi_reg: e.target.value })} placeholder="SEBI reg no (optional)"
                  className="col-span-2 md:col-span-1 bg-zinc-950 border border-white/[0.06] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/50" />
                <input value={form.aum} onChange={(e) => setForm({ ...form, aum: e.target.value })} placeholder="AUM range (optional)"
                  className="col-span-2 md:col-span-1 bg-zinc-950 border border-white/[0.06] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/50" />
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)"
                  className="col-span-2 bg-zinc-950 border border-white/[0.06] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/50" />
              </div>
              {error && <p className="text-xs text-rose-400">{error}</p>}
              <button type="submit" disabled={submitting}
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold rounded-lg py-3 text-sm transition-colors disabled:opacity-60">
                {submitting ? 'Sending…' : 'Request a demo'}
              </button>
              <p className="text-[11px] text-zinc-600 text-center">
                We&apos;ll only use your details to reach out. No newsletters.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
   FOOTER
   ──────────────────────────────────────────────────────────── */

function Footer() {
  return (
    <footer className="border-t border-white/[0.04]">
      <div className="max-w-6xl mx-auto px-6 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-2">
          <img src="/kubair-mark.png" alt="KubAIr" className="w-7 h-7 object-contain" />
          <span className="text-sm font-semibold text-white">Kub<span className="bg-gradient-to-r from-emerald-300 via-sky-300 to-violet-400 bg-clip-text text-transparent">AI</span>r</span>
        </div>
        <div className="flex gap-6 text-xs text-zinc-600">
          <a href="/login" className="hover:text-zinc-300 transition-colors">Log in</a>
          <a href="/signup" className="hover:text-zinc-300 transition-colors">Sign up</a>
          <a href="#features" className="hover:text-zinc-300 transition-colors">Features</a>
        </div>
        <p className="text-[11px] text-zinc-700 text-center md:text-right max-w-xs">
          Not investment advice. AI analysis for informational purposes only. © {new Date().getFullYear()} KubAIr.
        </p>
      </div>
    </footer>
  );
}

/* ────────────────────────────────────────────────────────────
   MARQUEE KEYFRAMES (injected inline)
   ──────────────────────────────────────────────────────────── */

const marqueeCSS = `
@keyframes marquee {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}
@keyframes marquee-reverse {
  0% { transform: translateX(-50%); }
  100% { transform: translateX(0); }
}
`;

/* ────────────────────────────────────────────────────────────
   PAGE
   ──────────────────────────────────────────────────────────── */

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 antialiased selection:bg-emerald-500/30 overflow-x-hidden">
      <style>{marqueeCSS}</style>
      <ParticleCanvas />
      <MouseSpotlight />
      <Navbar />
      <Hero />
      <Numbers />
      <BentoGrid />
      <DemoSection />
      <ToolsSection />
      <Audience />
      <AdvisorCTA />
      <CTA />
      <Footer />
    </div>
  );
}
